import { useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import {
  faBell,
  faCopyright,
  faEdit
} from '@fortawesome/pro-regular-svg-icons';
import { Accordion, ButtonText } from '@morressier/alembic';

import { IconBadge } from 'src/components/IconBadge';
import HighlightItem from '../PeerReview/HighlightItem';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { EditorialAuthorWithdrawalRequested } from '../common/WithdrawalRequested';
import { SubmitChangeRequest } from 'src/components/Submission/decisionModals/changeRequest/SubmitChangeRequest';
import { faRefresh } from '@fortawesome/pro-light-svg-icons';
import { ChangeRequestModal } from 'src/components/Submission/decisionModals/changeRequest/ChangeRequestLogModal';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';
import { pluralizeString } from 'src/utils/string';
import { SubmissionDetailTab } from 'src/pages/SubmissionDetails';
import { useSubmissionDetails } from 'src/pages/SubmissionDetails/SubmissionDetailsContext';
import ProtectedWorkflowAction from '../../../../ProtectedAction/ProtectedWorkflowAction';
import { getIcmjeDisclosureLabel } from '../../../../../utils/icmje';
import { BACKOFFICE_TABS } from '../../../BackofficeTab/BackofficeTab';
import { isFormConfigured } from '../../../../../utils/formConfig';

interface Props {
  setActiveTab: (tab: SubmissionDetailTab) => void;
}

export const EditorialPreProductionAdditionalInformation = ({
  setActiveTab
}: Props) => {
  const [showRequestUpdatesModal, setShowRequestUpdatesModal] = useState(false);
  const [showChangeRequestLog, setShowChangeRequestLog] = useState(false);

  const { changeRequestData, icmjeForm, icmjeFormList, submissionContext } =
    useSubmissionData();
  const { setBackOfficeSubTab } = useSubmissionDetails();

  const totalRequestedChanges =
    changeRequestData?.changeRequest?.requestHistory?.flatMap(
      history => history.fields
    ).length;

  const changeRequestCounts =
    changeRequestData?.changeRequest?.fields?.length ?? 0;
  const changeRequestTitle = `${changeRequestCounts} ${pluralizeString(
    'Unsent Request',
    changeRequestCounts
  )}`;

  return (
    <AdditionalInformationWrapper>
      {/* ----- Show Request Updates Modal ----- */}
      {showRequestUpdatesModal && (
        <SubmitChangeRequest
          onClose={() => setShowRequestUpdatesModal(false)}
        />
      )}

      {/* ----- Show Change Request Log -----  */}
      {showChangeRequestLog &&
      changeRequestData?.changeRequest?.requestHistory?.length ? (
        <ChangeRequestModal
          onClose={() => setShowChangeRequestLog(false)}
          changeRequestHistory={
            changeRequestData?.changeRequest?.requestHistory
          }
        />
      ) : null}

      <Flex flexDirection="column">
        {/* ----- Highlights ----- */}
        <Accordion
          chakraAccordionProps={{
            width: '100%',
            defaultIndex: 0,
            allowToggle: true
          }}
          items={[
            {
              title: (
                <Flex alignItems={'flex-start'} gap={2}>
                  <IconBadge icon={faBell} variant="neutral" />
                  <Flex flexDirection={'column'} marginLeft={2}>
                    <Text textStyle="headingItem">Highlights</Text>
                    <Text textStyle="textContext">
                      Please review key updates and other relevant information
                      in this phase.
                    </Text>
                  </Flex>
                </Flex>
              ),
              content: (
                <Flex flexDirection="column" gap={2}>
                  <EditorialAuthorWithdrawalRequested />

                  {(changeRequestData?.changeRequest?.requestHistory || [])
                    ?.length > 0 && (
                    <HighlightItem
                      variant="attention"
                      icon={faRefresh}
                      heading={`Awaiting ${totalRequestedChanges} Updates from Author`}
                      buttonOnClick={() => {
                        setShowChangeRequestLog(true);
                      }}
                      buttonText="View Details"
                    />
                  )}

                  <HighlightItem
                    variant="attention"
                    icon={faEdit}
                    heading={changeRequestTitle}
                    context="Select and communicate to the author any fields requiring their input."
                    customButton={
                      <ProtectedWorkflowAction actionName="EditorialSubmitChangeRequest#form">
                        <ButtonText
                          flexShrink="0"
                          onClick={() => setShowRequestUpdatesModal(true)}
                        >
                          Request Updates
                        </ButtonText>
                      </ProtectedWorkflowAction>
                    }
                  />

                  {icmjeFormList?.length &&
                    isFormConfigured(
                      submissionContext?.config?.authorICMJEModel
                    ) && (
                      <HighlightItem
                        variant="attention"
                        icon={faCopyright}
                        heading={getIcmjeDisclosureLabel(
                          icmjeFormList ?? [],
                          icmjeForm ?? []
                        )}
                        buttonOnClick={() => {
                          setActiveTab('backoffice');
                          setBackOfficeSubTab(BACKOFFICE_TABS.ICMJE_DISCLOSURE);
                        }}
                        buttonText="Review Info"
                      />
                    )}
                </Flex>
              )
            }
          ]}
        />
      </Flex>
    </AdditionalInformationWrapper>
  );
};
